import Phaser from 'phaser';

/**
 * HTML <input> element positioned over the Phaser canvas at design-
 * space coordinates. Phaser has no native text input — for real forms
 * (auth scenes, eventual settings) we paint the input on top in HTML
 * and translate design coords to viewport pixels.
 *
 * Lifecycle: the input mounts into #overlay-layer (sibling of #app in
 * index.html), positions itself on construction + on window resize,
 * cleans up automatically when the owning scene shuts down. Callers
 * don't need to wire shutdown manually.
 *
 * Touch behavior on iOS: the input's font-size is floored at 16px on
 * screen so iOS Safari doesn't auto-zoom on focus — a UX-breaking
 * default that's only avoidable by ensuring the input renders at
 * 16px or larger.
 */

const OVERLAY_LAYER_ID = 'overlay-layer';
const IOS_NO_ZOOM_FONT_PX = 16;

export type TextInputType = 'text' | 'email' | 'password';

export interface TextInputOverlayOpts {
  readonly scene: Phaser.Scene;
  /** Center x in design-canvas coordinates (0..DESIGN_WIDTH). */
  readonly x: number;
  /** Center y in design-canvas coordinates (0..DESIGN_HEIGHT). */
  readonly y: number;
  /** Width in design-canvas units. */
  readonly width: number;
  /** Height in design-canvas units. */
  readonly height: number;
  readonly type?: TextInputType;
  readonly placeholder?: string;
  /** HTML autocomplete attribute. Use 'email', 'current-password',
   *  'new-password', etc. so iOS / 1Password offer the right autofill. */
  readonly autocomplete?: string;
  readonly value?: string;
  readonly maxLength?: number;
  /** Design-space font size; floored at 16 device pixels regardless
   *  so iOS doesn't auto-zoom on focus. */
  readonly fontSizeDesign?: number;
  readonly onChange?: (value: string) => void;
  /** Fired on Enter keydown. */
  readonly onSubmit?: () => void;
}

export class TextInputOverlay {
  private readonly el: HTMLInputElement;
  private readonly scene: Phaser.Scene;
  private readonly opts: TextInputOverlayOpts;
  private readonly resizeHandler: () => void;
  private destroyed = false;

  constructor(opts: TextInputOverlayOpts) {
    this.scene = opts.scene;
    this.opts = opts;

    this.el = document.createElement('input');
    this.el.type = opts.type ?? 'text';
    if (opts.placeholder) this.el.placeholder = opts.placeholder;
    // setAttribute (not the typed property) because the DOM lib
    // narrows HTMLInputElement.autocomplete to a strict AutoFill
    // union — passing "email" / "current-password" works at runtime
    // but trips TS. The HTML attribute itself accepts any string.
    if (opts.autocomplete)
      this.el.setAttribute('autocomplete', opts.autocomplete);
    if (opts.value) this.el.value = opts.value;
    if (opts.maxLength) this.el.maxLength = opts.maxLength;
    // Disable autocapitalize/autocorrect on email/password — iOS
    // capitalizes the first letter of an email by default, which
    // breaks sign-in for users whose email is lowercase.
    if (opts.type === 'email' || opts.type === 'password') {
      this.el.autocapitalize = 'off';
      this.el.spellcheck = false;
    }

    // Styling matches the app's dark/cyan aesthetic. Inline so the
    // overlay doesn't need a separate stylesheet shipped + so we
    // can read the palette from the same source of truth used by
    // Phaser-side UI (the COLORS constants).
    Object.assign(this.el.style, {
      boxSizing: 'border-box',
      background: '#0e1a2b',
      border: '1px solid #2e4f7a',
      borderRadius: '4px',
      color: '#e8eef5',
      fontFamily: '"Kode Mono", "Consolas", monospace',
      outline: 'none',
      padding: '0 14px',
      letterSpacing: '1px',
      transition: 'border-color 0.15s',
    });
    this.el.addEventListener('focus', () => {
      this.el.style.borderColor = '#4fc3ff';
    });
    this.el.addEventListener('blur', () => {
      this.el.style.borderColor = '#2e4f7a';
    });
    this.el.addEventListener('input', () => {
      opts.onChange?.(this.el.value);
    });
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        opts.onSubmit?.();
      }
    });

    const overlay = document.getElementById(OVERLAY_LAYER_ID);
    if (overlay) {
      overlay.appendChild(this.el);
    } else {
      // Defensive: should never happen (index.html owns the div), but
      // logging here is better than a silent broken form.
      console.warn(`TextInputOverlay: #${OVERLAY_LAYER_ID} not found`);
      document.body.appendChild(this.el);
    }

    this.position();

    this.resizeHandler = (): void => this.position();
    window.addEventListener('resize', this.resizeHandler);

    // Auto-cleanup on scene shutdown so callers don't need to
    // remember to call .destroy() manually.
    this.scene.events.once(
      Phaser.Scenes.Events.SHUTDOWN,
      () => this.destroy()
    );
  }

  /**
   * Re-anchor the input over the canvas. Called on construction and
   * on every window resize. Reads the canvas's actual screen rect
   * via getBoundingClientRect() rather than Phaser.Scale internals
   * for cross-version stability.
   */
  private position(): void {
    if (this.destroyed) return;
    const canvas = this.scene.scale.canvas;
    const rect = canvas.getBoundingClientRect();
    const designSize = this.scene.scale.gameSize;
    const scaleX = rect.width / designSize.width;
    const scaleY = rect.height / designSize.height;

    const w = this.opts.width * scaleX;
    const h = this.opts.height * scaleY;
    const left = rect.left + (this.opts.x - this.opts.width / 2) * scaleX;
    const top = rect.top + (this.opts.y - this.opts.height / 2) * scaleY;

    const fontSize = Math.max(
      IOS_NO_ZOOM_FONT_PX,
      (this.opts.fontSizeDesign ?? 24) * scaleY
    );

    this.el.style.width = `${w}px`;
    this.el.style.height = `${h}px`;
    this.el.style.left = `${left}px`;
    this.el.style.top = `${top}px`;
    this.el.style.fontSize = `${fontSize}px`;
  }

  getValue(): string {
    return this.el.value;
  }

  setValue(v: string): void {
    this.el.value = v;
  }

  focus(): void {
    // setTimeout 0 yields to the layout/paint cycle so the input is
    // mounted + sized BEFORE we ask the browser to focus it. Without
    // this, focus sometimes fires before the input is interactive
    // and Safari ignores it.
    setTimeout(() => {
      if (!this.destroyed) this.el.focus();
    }, 0);
  }

  setDisabled(disabled: boolean): void {
    this.el.disabled = disabled;
    this.el.style.opacity = disabled ? '0.5' : '1';
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener('resize', this.resizeHandler);
    this.el.remove();
  }
}
